import type { StagedActionEditTagOption, StagedActionView } from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'

/** A view tag's display label paired with the canonical value written back. */
export interface TagValuePair {
  /** What the user sees on the chip (the view tag's value). */
  label: string
  /** What gets written back in a `tagEdits` PATCH (e.g. an email for `Guest`). */
  value: string
}

/**
 * Reconcile a tag group's LIVE view tags with its edit-tag `options` to recover
 * the canonical value to write back for each item.
 *
 * Why this is needed: for most editable fields a view tag's `value` IS the value
 * written back, but some fields show a display label that differs from it —
 * notably `Guest`, whose view tag shows the resolved contact NAME while the
 * value written back must be the EMAIL. The edit-tag `options` carry that
 * mapping (`name` = label, `value` = canonical). Reading the value straight off
 * the view tag would round-trip the name and the backend drops every token
 * without an `@`, wiping previously-resolved attendees ("adding a guest clears
 * the others").
 *
 * The view tags are the LIVE list — updated from each PATCH response — while the
 * options refetch asynchronously, so the two briefly diverge after an edit. We
 * therefore always iterate the LIVE view tags (so adds/removes are reflected
 * immediately) and only consult the options for the canonical value:
 *
 *  - In sync (the view tags' labels equal the options' names in order — both
 *    derive from the same source list, so this holds exactly when the options
 *    describe the current roster): pair by INDEX. Exact even when two items
 *    share a display label.
 *  - Diverged (a guest edit updated the view tags but the options query hasn't
 *    refetched yet — the label sequences differ, e.g. an add, a removal, or a
 *    same-length replacement): map by the display label the view tag shows. A
 *    removed item simply isn't iterated; an added/replaced item not yet in the
 *    options falls back to its view value. We do NOT index-pair here: a
 *    same-length replacement would otherwise map the new item to the removed
 *    one's canonical value (e.g. a swapped guest's free/busy hitting the wrong
 *    email) until the options refetch.
 *
 * Plain-text multiText (no options) falls through to the view value, preserving
 * the identity behaviour for fields where label === value.
 */
export function tagCanonicalValues(
  tags: Array<StagedActionView['tags'][number]>,
  options: Array<StagedActionEditTagOption>,
): Array<TagValuePair> {
  const inSync = tags.length === options.length && tags.every((tag, i) => tag.value === options[i].name)
  if (inSync) {
    return tags.map((tag, i) => ({ label: tag.value, value: options[i].value }))
  }
  // Diverged: map by display label, but CONSUME each label's options in order
  // (FIFO) rather than last-wins, so two guests sharing a display name don't
  // collapse onto one email — each gets a distinct same-named option's value,
  // and any extra live tag past the available options falls back to its view
  // value (e.g. a just-added guest not yet in the options).
  const queueByLabel = new Map<string, Array<string>>()
  for (const o of options) {
    const queue = queueByLabel.get(o.name)
    if (queue) queue.push(o.value)
    else queueByLabel.set(o.name, [o.value])
  }
  return tags.map((tag) => {
    const queue = queueByLabel.get(tag.value)
    const value = queue && queue.length > 0 ? queue.shift()! : tag.value
    return { label: tag.value, value }
  })
}
